import { request } from './client';

/**
 * 创建标签
 * @param {Object} payload - 标签数据
 */
export const createLabel = async (payload) => {
  return request('/labels', {
    method: 'POST',
    body: payload,
  });
};

/**
 * 获取标签列表
 * @param {Object} params - {pageNum, pageSize}
 */
export const fetchLabels = async ({ pageNum = 1, pageSize = 10 } = {}) => {
  return request('/labels', {
    method: 'GET',
    params: { pageNum, pageSize },
  });
};

/**
 * 获取标签详情
 * @param {number} id - 标签ID
 * @param {boolean} lite - 精简模式（不含大字段，用于快速首屏渲染）
 */
export const fetchLabelDetail = async (id, lite = false, options = {}) => {
  return request(`/labels/${id}`, {
    method: 'GET',
    params: lite ? { lite: true } : undefined,
    ...options,
  });
};

/**
 * 获取单个内容项完整数据（用于懒加载）
 * @param {number} labelId - 标签ID
 * @param {string} itemId - 内容项ID
 */
export const fetchContentItem = async (labelId, itemId, options = {}) => {
  return request(`/labels/${labelId}/content/${itemId}`, {
    method: 'GET',
    ...options,
  });
};

/**
 * 更新标签
 * @param {number} id - 标签ID
 * @param {Object} payload - 标签数据
 */
export const updateLabel = async (id, payload) => {
  return request(`/labels/${id}`, {
    method: 'PUT',
    body: payload,
  });
};

/**
 * 更新已发布标签的生产信息
 * @param {number} id - 标签ID
 * @param {{productionDate?: string, batchNo?: string, expiryDate?: string}} payload
 */
export const updateLabelProductionInfo = async (id, payload) => {
  return request(`/foodlabel/labels/${id}/production-info`, {
    method: 'POST',
    body: payload,
  });
};

/**
 * 发布标签
 * @param {number} id - 标签ID
 */
export const publishLabel = async (id, payload) => {
  return request(`/labels/${id}/publish`, {
    method: 'POST',
    body: payload,
  });
};

/**
 * 克隆标签
 * @param {number} id - 标签ID
 */
export const cloneLabel = async (id) => {
  return request(`/labels/${id}/clone`, {
    method: 'POST',
  });
};

/**
 * 基于已发布/已下架标签创建修订草稿
 * @param {number} id - 原标签ID
 */
export const createRevisionDraft = async (id) => {
  return request(`/labels/${id}/revise`, {
    method: 'POST',
  });
};

/**
 * 删除标签
 * @param {number} id - 标签ID
 */
export const deleteLabel = async (id) => {
  return request(`/labels/${id}`, {
    method: 'DELETE',
  });
};

/**
 * 生成二维码
 * @param {number} id - 标签ID
 */
export const generateQRCode = async (id) => {
  return request(`/labels/${id}/qrcode`, {
    method: 'GET',
  });
};

/**
 * 手动刷新二维码
 * @param {number} id - 标签ID
 */
export const refreshQRCode = async (id) => {
  return request(`/labels/${id}/qrcode/refresh`, {
    method: 'POST',
  });
};

/**
 * 获取二维码基础URL配置
 */
export const getQRCodeBaseUrl = async () => {
  return request(`/labels/config/qrcode-base-url`, {
    method: 'GET',
  });
};

/**
 * 提交标签审核（创建AI审核任务）
 * @param {number} labelId - 标签ID
 */
export const submitForReview = async (labelId) => {
  return request(`/api/label/audit-task/ai-review`, {
    method: 'POST',
    body: { labelId },
    skipBaseUrl: true,
  });
};

/**
 * 驳回后重新提交审核（跳过AI审核，直接进入人工复核）
 * @param {number} labelId - 标签ID
 */
export const resubmitForReview = async (labelId) => {
  return request(`/api/label/audit-task/resubmit`, {
    method: 'POST',
    body: { labelId },
    skipBaseUrl: true,
  });
};

/**
 * 获取标签审核状态
 * @param {number} labelId - 标签ID
 */
export const getLabelReviewStatus = async (labelId) => {
  return request(`/api/label/audit-task/label/${labelId}/current`, {
    method: 'GET',
    skipBaseUrl: true,
  });
};

/**
 * 获取用户标签统计
 * @returns {Promise<{totalLabels: number, pendingLabels: number, publishedLabels: number}>}
 */
export const getUserLabelStats = async () => {
  return request('/labels/stats/user', {
    method: 'GET',
  });
};

/**
 * 生成标签语音（调用后端阿里云TTS）
 * @param {number} labelId
 * @param {{text:string, voice?:string, format?:string, sampleRate?:number}} payload
 */
export const generateLabelVoice = async (labelId, payload) => {
  return request(`/labels/${labelId}/voice`, {
    method: 'POST',
    body: payload,
  });
};

/**
 * 获取标签最新语音文件
 * @param {number} labelId
 */
export const fetchLabelVoice = async (labelId) => {
  return request(`/labels/${labelId}/voice`, {
    method: 'GET',
    skipAuth: true,
  });
};

/**
 * 批量删除标签
 * @param {number[]} ids - 标签ID列表
 */
export const batchDeleteLabels = async (ids) => {
  return request('/labels/batch/delete', {
    method: 'POST',
    body: { labelIds: ids },
  });
};
